import { useMemo } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useCompany } from '@/contexts/CompanyContext';
import { useFeedbackConfig } from '@/providers/FeedbackConfigProvider';
import type { FeedbackCollectorParams } from '@/types/feedbackCollector';

/**
 * Custom hook to build the feedback collector component parameters.
 *
 * Sources userId from AuthProvider, customerName from CompanyContext,
 * and feedback settings from FeedbackConfigProvider.
 *
 * @param feature - The feature name to collect feedback for (e.g., "Homepage", "Projects")
 * @returns The oc-component parameters, or null if feedback config is unavailable
 */
export function useFeedbackCollectorParams(feature: string): FeedbackCollectorParams | null {
  const { authenticatedUser, instanceId } = useAuth();
  const { currentCompany } = useCompany();
  const config = useFeedbackConfig();

  const customerName = currentCompany?.name ?? instanceId ?? 'Unknown';
  const userId = authenticatedUser?.email ?? 'Unknown';
  const orgId = instanceId ?? '';

  const accountToken = config?.accountToken;
  const apiHost = config?.apiHost;
  const reDisplayAfterDays = config?.reDisplayAfterDays;
  const mfeRegistryBase = config?.mfeRegistryBase;
  const mfeVersion = config?.mfeVersion;
  const product = config?.product;

  return useMemo(() => {
    if (!accountToken || !apiHost || !mfeRegistryBase || reDisplayAfterDays === undefined) return null;

    return {
      feature,
      accountToken,
      apiHost,
      reDisplayAfterDays,
      mfeRegistryBase,
      mfeVersion,
      customerName,
      userId,
      orgId,
      product,
    };
  }, [
    accountToken,
    apiHost,
    customerName,
    feature,
    mfeRegistryBase,
    mfeVersion,
    orgId,
    product,
    reDisplayAfterDays,
    userId,
  ]);
}
